import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import '../styles/EnhancedHeader.css';

/**
 * ENHANCED PAGE HEADER
 * Features: Background image, animated particles, parallax effect, gradient overlay
 */

const EnhancedHeader = ({ 
  title = 'About Active Care Residential',
  subtitle = 'Building Futures, Supporting Lives',
  backgroundImage = '/images/residential-care-banner.jpg', // Path to your image
  backgroundGradient = 'linear-gradient(135deg, rgba(52, 152, 219, 0.7) 0%, rgba(26, 188, 156, 0.7) 100%)'
}) => {
  const [particles, setParticles] = useState([]);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  // Generate random particles on mount
  useEffect(() => {
    const particleArray = Array.from({ length: 20 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      top: Math.random() * 100,
      size: Math.random() * 3 + 1,
      duration: Math.random() * 15 + 8,
      delay: Math.random() * 3,
      opacity: Math.random() * 0.4 + 0.1,
    }));
    setParticles(particleArray);
  }, []);

  // Track mouse position for parallax
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Calculate parallax offset
  const parallaxOffset = {
    x: (mousePosition.x - window.innerWidth / 2) * 0.01,
    y: (mousePosition.y - window.innerHeight / 2) * 0.01,
  };

  const titleVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut',
      },
    },
  };

  const subtitleVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        delay: 0.2,
        ease: 'easeOut',
      },
    },
  };

  return (
    <section className="enhanced-header-wrapper">
      {/* Background Image */}
      <div 
        className="header-background-image"
        style={{
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />

      {/* Gradient Overlay */}
      <div 
        className="header-gradient-overlay"
        style={{
          background: backgroundGradient,
        }}
      />

      {/* Animated Particles */}
      <div className="header-particles-container">
        {particles.map((particle) => (
          <motion.div
            key={particle.id}
            className="header-particle"
            style={{
              left: `${particle.left}%`,
              top: `${particle.top}%`,
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              opacity: particle.opacity,
            }}
            animate={{
              y: [0, -30, 0],
              x: [0, Math.random() * 20 - 10, 0],
            }}
            transition={{
              duration: particle.duration,
              delay: particle.delay,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        ))}
      </div>

      {/* Parallax Shape */}
      <motion.div
        className="header-parallax-shape"
        style={{
          x: parallaxOffset.x,
          y: parallaxOffset.y,
        }}
      />

      {/* Content */}
      <div className="enhanced-header-content">
        <motion.h1
          className="enhanced-page-title"
          variants={titleVariants}
          initial="hidden"
          animate="visible"
        >
          {title}
        </motion.h1>

        <motion.p
          className="enhanced-page-subtitle"
          variants={subtitleVariants}
          initial="hidden"
          animate="visible"
        >
          {subtitle}
        </motion.p>

        {/* Decorative Line */}
        <motion.div
          className="header-divider"
          initial={{ width: 0 }}
          animate={{ width: 100 }}
          transition={{ duration: 1, delay: 0.4 }}
        />
      </div>

      {/* Scroll Indicator */}
      <motion.div
        className="header-scroll-indicator"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M12 5v14M5 12l7 7 7-7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </motion.div>
    </section>
  );
};

export default EnhancedHeader;
